"use client";
import React, { useState, useRef, useLayoutEffect } from "react";

interface TabContent {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface AllAboutSectionProps {
  title: string;
  highlightedWord: string;
  tabs: TabContent[];
}

const AllAboutSection: React.FC<AllAboutSectionProps> = ({
  title,
  highlightedWord,
  tabs,
}) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const activeContent = tabs.find((tab) => tab.id === activeTab);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setIsExpanded(false);
  };

  // Measure if content overflows the clamp
  useLayoutEffect(() => {
    if (contentRef.current) {
      const el = contentRef.current;
      // Only check overflow when NOT expanded
      if (!isExpanded) {
        setNeedsReadMore(el.scrollHeight > el.clientHeight + 1); // +1 to avoid tiny rounding errors
      }
    }
  }, [activeTab, isExpanded, activeContent]);

  const renderContentWithReadMore = (content: React.ReactNode) => {
    return (
      <div className="relative">
        <div
          ref={contentRef}
          className="overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: isExpanded ? "none" : 5,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.6",
          }}
        >
          {content}
        </div>

        {needsReadMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[#3399FF] font-medium hover:underline md:mt-2 inline-block"
          >
            {isExpanded ? "Read Less" : "Read More"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white w-full py-6 md:py-12 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="font-semibold relative inline-block text-[20px] md:text-[32px] tracking-[-0.01em]">
            <span className="text-black">{title} </span>
            <span className="bg-[linear-gradient(91deg,#39F_81.57%,#FFBA08_106.77%)] bg-clip-text text-transparent">
              {highlightedWord}
            </span>
            <span
              aria-hidden
              className="pointer-events-none absolute md:left-[0px] left-[20px] md:right-[68px] right-[70px]
                -bottom-[4px] h-[2px] bg-[#FFB801] rounded-[5px] z-10"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-[4px] md:right-[42px] right-[41px]
                h-[2px] w-6 bg-[#FFB801] rounded-[5px] z-10"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-[4px] md:right-[20px] right-[20px]
                h-[2px] w-4 bg-[#FFB801] rounded-[5px] z-10"
            />
          </h2>
        </div>

        {/* Tabs - Horizontal Scroll */}
        <div className="mb-8 overflow-hidden">
          <div
            className="flex gap-3 md:gap-4 overflow-x-auto pb-2"
            style={{
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {tabs?.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-shrink-0 px-2 md:px-3 py-1 rounded-2xl text-sm md:text-base font-medium transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-[#3399FF] text-white shadow-lg"
                    : "bg-white text-[#3399FF] border border-[#C9C9C9] hover:bg-[#3399FF] hover:fade-out-30 hover:text-white"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="rounded-lg" style={{ backgroundColor: "#F7FBFF" }}>
          <div className="w-full max-w-[336px] md:max-w-[1120px] mx-auto p-2 md:p-4">
            <div className="prose prose-sm md:prose-base max-w-none text-gray-800">
              {renderContentWithReadMore(activeContent?.content)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllAboutSection;
